import useApi from '@propertypal/shared/src/hooks/useApi';
import useDebounce from '@propertypal/shared/src/hooks/useDebounce';
import OgImage from '@propertypal/shared/src/resources/images/soldprice-checker/open-graph.jpg';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { ROISoldPrices } from '@propertypal/shared/src/types/soldPriceData';
import SoldPriceHeader from '@propertypal/web-ui/src/price-tracker/SoldPriceHeader';
import SoldPriceTable from '@propertypal/web-ui/src/price-tracker/SoldPriceTable';
import { SubHeading } from '@propertypal/web-ui/src/typography';
import Head from 'next/head';
import React, { FunctionComponent, useEffect, useState } from 'react';
import generateMetaTags from '../components/layout/MetaTags';
import { requireAuthToken } from '../utils/auth';

interface Props {
  soldPrices: ROISoldPrices;
}

const DEBOUNCE_TIME = process?.env?.JEST_WORKER_ID ? 0 : 1000;

export const getServerSideProps = requireAuthToken(async (ctx) => {
  const result = await axios(
    {
      method: 'GET',
      url: `${API_URL}/roi-sold-prices/search?autoRadius=true`,
    },
    undefined,
    ctx,
  );

  return {
    props: {
      soldPrices: result.data,
    },
  };
});

const SoldPriceChecker: FunctionComponent<Props> = (props) => {
  const [radius, setRadius] = useState(props.soldPrices.radius);
  const [query, setQuery] = useState('');
  const [soldPrices, , loading, fetchSoldPrices, setSoldPrices] = useApi(`/roi-sold-prices/search`, false);
  const debounceFetch = useDebounce(fetchSoldPrices, DEBOUNCE_TIME, { leading: false, trailing: true }, []);

  useEffect(() => {
    if (!query) {
      setSoldPrices(props.soldPrices);
    } else {
      debounceFetch(`/roi-sold-prices/search?radius=${radius}&query=${encodeURIComponent(query)}`);
    }
  }, [radius, query]);

  return (
    <>
      <Head>
        {generateMetaTags({
          title: 'Property Pal - Sold Price Checker',
          description: 'Sold Prices ROI',
          url: '/soldprice-checker',
          image: {
            url: OgImage.url,
            width: 1200,
            height: 650,
          },
        })}
      </Head>

      <SoldPriceHeader query={query} setQuery={setQuery} radius={radius} setRadius={setRadius} />

      <SubHeading align="center" mt={60}>
        RECENTLY SOLD PROPERTIES
      </SubHeading>

      <SoldPriceTable loading={loading} soldData={soldPrices ? soldPrices.results : props.soldPrices.results} />
    </>
  );
};

export default SoldPriceChecker;
